import type { ChecklistAdviceStatus, ChecklistSeverity } from '@arhs-fleetback/types/entities/checklists';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { INTERVENTION_MODE } from 'constants/InterventionConstants';
import type { ChecklistGroupStatusEnum } from 'modules/checklists/types/resources/checklists.enum';
import { createDictionary } from 'utils/ObjectUtils';

import { AFTER_SALES, PARTS, SALES, SELF_SERVICE } from './ExtensionConstants';
import { TECHNICIAN } from './ModeConstants';
import {
  TIRES,
  REMINDER,
  BODYWORK,
  STOCK_VO,
  MECHANIC,
  GUARANTEE,
  SALES_MESSAGE,
  PARTS_REMINDER,
  BODYWORK_APPOINTMENT,
  COMMUNICATION
} from './ModuleConstants';
import { PATH } from './RoutesConstants';

export const CHECKLIST_WORK = 'WORK';
export const CHECKLIST_DONE = 'DONE';
export const CHECKLIST_BILLING = 'BILLING';
export const CHECKLIST_ISSUES = 'ISSUES';
export const CHECKLIST_DOCUMENTS = 'DOCUMENTS';
export const CHECKLIST_SCHEDULED = 'SCHEDULED';
export const CHECKLIST_REMINDER_SCHEDULED = 'REMINDER_SCHEDULED';
export const CHECKLIST_VEHICLE = 'VEHICLE';
export const CHECKLIST_ACCESSORY = 'ACCESSORY';
const CHECKLIST_TRADE_IN = 'TRADE_IN';
export const CHECKLIST_DISCOUNT = 'DISCOUNT';
export const CHECKLIST_UPSELLING = 'UPSELLING';
export const CHECKLIST_PARTS = 'PARTS';
const PARTS_REMINDER_SCHEDULED = 'PARTS_REMINDER_SCHEDULED';
const PREVIOUSLY_PROPOSED_WORK = 'PREVIOUSLY_PROPOSED_WORK';

export const TYPES = {
  WORKS: CHECKLIST_WORK,
  DONE: CHECKLIST_DONE,
  BILLING: CHECKLIST_BILLING,
  ISSUES: CHECKLIST_ISSUES,
  DOCUMENTS: CHECKLIST_DOCUMENTS,
  SCHEDULED: CHECKLIST_SCHEDULED,
  REMINDER_SCHEDULED: CHECKLIST_REMINDER_SCHEDULED,
  VEHICLE: CHECKLIST_VEHICLE,
  ACCESSORY: CHECKLIST_ACCESSORY,
  TRADE_IN: CHECKLIST_TRADE_IN,
  DISCOUNT: CHECKLIST_DISCOUNT,
  UPSELLING: CHECKLIST_UPSELLING,
  PARTS: CHECKLIST_PARTS,
  PARTS_REMINDER_SCHEDULED,
  PREVIOUSLY_PROPOSED_WORK,
};

export const SEVERITY = {
  UNDEFINED: 'UNDEFINED',
  NONE: 'NONE',
  LOW: 'LOW',
  HIGH: 'HIGH',
  OFFERED: 'OFFERED'
} as const satisfies Partial<Record<ChecklistSeverity, ChecklistSeverity>>;

export const PROACTIVE_SEVERITIES = [
  SEVERITY.LOW,
  SEVERITY.HIGH,
  SEVERITY.OFFERED
];

export const SALES_SEVERITY = {
  UNDEFINED: 'UNDEFINED',
  PROPOSED: 'PROPOSED',
  OFFERED: 'OFFERED'
};

export const ALL_SEVERITIES = [
  SEVERITY.UNDEFINED,
  SEVERITY.NONE,
  SEVERITY.LOW,
  SEVERITY.HIGH,
  SEVERITY.OFFERED,
  SALES_SEVERITY.PROPOSED
];

export const SEVERITY_BY_CHECKLIST_TYPE = {
  [CHECKLIST_WORK]: Object.values(SEVERITY),
  [CHECKLIST_PARTS]: Object.values(SALES_SEVERITY),
  [CHECKLIST_ACCESSORY]: Object.values(SALES_SEVERITY),
  DEFAULT: ALL_SEVERITIES
};

export const ANSWERABLE_SEVERITIES = [SEVERITY.LOW, SEVERITY.HIGH, SALES_SEVERITY.PROPOSED];

export const DEFAULT_CHECKLIST_NAME = 'Default';
export const PROACTIVE_CHECKLIST_NAME = 'Proactive';

export const DEFAULT_ITEM_NAME = {
  en_us: 'Others',
  en_gb: 'Others',
  de: 'Andere',
  fr: 'Autres',
  es: 'Otros',
  nl: 'Ander',
  sv: 'Annat',
  it: 'Altri',
  no: 'Andre',
  pt: 'Outros',
  pl: 'Inne',
  ru: 'Другие',
  uk: 'Інші',
  ro_ro: 'Altele',
  hr_hr: 'Ostalo',
  fi_fi: 'Muut',
  et_ee: 'Muud',
  el_gr: 'Άλλα',
  da_dk: 'Andre',
  ja_jp: 'その他',
  lt_lt: 'Kiti',
  lv_lv: 'Citi'
};
export const DEFAULT_OPENED_ADVICES = [SEVERITY.LOW, SEVERITY.HIGH, SEVERITY.OFFERED, SALES_SEVERITY.PROPOSED];

export const CHECKLIST_PAYMENT_STATUS = {
  FULL: 'FULL',
  DEPOSIT: 'DEPOSIT',
  NONE: 'NONE'
};

export const ALL_CHECKLIST_TYPE = [
  CHECKLIST_WORK,
  CHECKLIST_DONE,
  CHECKLIST_SCHEDULED,
  CHECKLIST_REMINDER_SCHEDULED,
  CHECKLIST_VEHICLE,
  CHECKLIST_ACCESSORY,
  CHECKLIST_TRADE_IN,
  CHECKLIST_DISCOUNT,
  CHECKLIST_PARTS,
  PARTS_REMINDER_SCHEDULED,
  PREVIOUSLY_PROPOSED_WORK,
];

// Order is important here
export const SALES_OFFER_CHECKLISTS = [CHECKLIST_VEHICLE, CHECKLIST_ACCESSORY, CHECKLIST_DISCOUNT, CHECKLIST_TRADE_IN];

export const CHECKLIST_TYPE_WITHOUT_SEVERITY = [
  CHECKLIST_DONE,
  CHECKLIST_SCHEDULED,
  CHECKLIST_BILLING,
  CHECKLIST_TRADE_IN,
  CHECKLIST_DISCOUNT
];

export const CATEGORIES = {
  AC: 'AC',
  ACCESSORY: 'ACCESSORY',
  BACK_CHASSIS: 'BACK_CHASSIS',
  BATTERY: 'BATTERY',
  BODYWORK: 'BODYWORK',
  BRAKE: 'BRAKE',
  CAR: 'CAR',
  DISCOUNT: 'DISCOUNT',
  ENGINE: 'ENGINE',
  FRONT_CHASSIS: 'FRONT_CHASSIS',
  INFOTAINMENT: 'INFOTAINMENT',
  LIGHT: 'LIGHT',
  MAINTENANCE: 'MAINTENANCE',
  OPTION: 'OPTION',
  SERVICE: 'SERVICE',
  TIRE: 'TIRE',
  TRADE_IN: 'TRADE_IN',
  TRANSMISSION: 'TRANSMISSION',
  OTHER: 'OTHER'
};

/**
 * @type {Record<
 *   import("@arhs-fleetback/types/entities/checklists").ChecklistAdviceStatus,
 *   import("@arhs-fleetback/types/entities/checklists").ChecklistAdviceStatus
 * >}
 */
export const ADVICE_STATUS = {
  ACCEPTED: 'ACCEPTED',
  PAID: 'PAID',
  PENDING: 'PENDING',
  REFUSED: 'REFUSED',
  DEPOSIT_PAID: 'DEPOSIT_PAID'
} as const satisfies Record<ChecklistAdviceStatus, ChecklistAdviceStatus>;

/**
 * @type {Record<
 *   import("modules/checklists/types/resources/advices/checklist-advice").ChecklistAdviceStateEnum,
 *   import("modules/checklists/types/resources/advices/checklist-advice").ChecklistAdviceStateEnum
 * >}
 */
export const ADVICE_STATE = {
  DONE: 'DONE',
  NOT_DONE: 'NOT_DONE'
};

export const CHECKLIST_STATUS = {
  PENDING: 'PENDING',
  REFUSED: 'REFUSED',
  ACCEPTED: 'ACCEPTED'
} as const satisfies Record<ChecklistGroupStatusEnum, ChecklistGroupStatusEnum>;

export const DEFAULT_ADVICE_CATEGORY = {
  [CHECKLIST_ACCESSORY]: CATEGORIES.ACCESSORY
};

export const INFORMATION_BY_TYPE = createDictionary(
  {
    [CHECKLIST_WORK]: 'page.config.dealer.checklists.info_title',
    [CHECKLIST_PARTS]: 'page.config.dealer.parts.info_title',
    [CHECKLIST_ACCESSORY]: 'page.config.dealer.sales.info_title'
  },
  'information'
);

export const DETAILS_PATH_BY_TYPE = createDictionary(
  {
    [CHECKLIST_ACCESSORY]: PATH.OFFERS_DETAILED, // Sales offers
    [CHECKLIST_PARTS]: PATH.PARTS_OFFERS_DETAILS, // Parts offers
    [CHECKLIST_UPSELLING]: PATH.DEALER_EXTRA_DETAILED // Self service extras
  },
  PATH.CHECKLISTS_DETAILED // Works
);

export const GROUP_DETAILS_PATH_BY_TYPE = createDictionary(
  {
    [CHECKLIST_ACCESSORY]: PATH.GROUP_OFFERS_DETAILED, // Sales offers
    [CHECKLIST_PARTS]: PATH.GROUP_PARTS_OFFERS_DETAILS, // Parts offers
    [CHECKLIST_UPSELLING]: PATH.GROUP_EXTRA_DETAILED // Self service extras
  },
  PATH.GROUP_CHECKLISTS_DETAILED // Works
);

export const EXTENSION_TO_CHECKLIST_TYPES = {
  [PARTS]: [CHECKLIST_PARTS],
  [SALES]: [CHECKLIST_ACCESSORY],
  [AFTER_SALES]: [CHECKLIST_WORK],
  [SELF_SERVICE]: [CHECKLIST_UPSELLING]
};

export const DEFAULT_SEVERITY_BY_MODE = {
  [INTERVENTION_MODE.TECHNICIAN]: SEVERITY.LOW,
  [INTERVENTION_MODE.SALES]: SALES_SEVERITY.PROPOSED,
  [INTERVENTION_MODE.PARTS]: SALES_SEVERITY.PROPOSED
};

export const CHECKLIST_TYPE_BY_MODE_AND_MODULES = {
  [INTERVENTION_MODE.SALES]: createDictionary(
    {
      [STOCK_VO]: TYPES.ACCESSORY,
      [SALES_MESSAGE]: TYPES.ACCESSORY
    },
    TYPES.ACCESSORY
  ),
  [INTERVENTION_MODE.TECHNICIAN]: createDictionary(
    {
      [GUARANTEE]: TYPES.WORKS,
      [BODYWORK]: TYPES.WORKS,
      [MECHANIC]: TYPES.WORKS,
      [TIRES]: TYPES.WORKS,
      [COMMUNICATION]: TYPES.WORKS,
      [BODYWORK_APPOINTMENT]: TYPES.WORKS,
      [REMINDER]: TYPES.REMINDER_SCHEDULED
    },
    TYPES.WORKS
  ),
  [INTERVENTION_MODE.PARTS]: createDictionary(
    {
      [PARTS]: TYPES.PARTS,
      [PARTS_REMINDER]: TYPES.PARTS_REMINDER_SCHEDULED
    },
    TYPES.PARTS
  )
};

export const MODE_BY_CHECKLIST_TYPE = Object.entries(CHECKLIST_TYPE_BY_MODE_AND_MODULES).reduce(
  (acc, [mode, dictionary]) => ({
    ...acc,
    [dictionary.DEFAULT]: mode,
    ...Object.values(dictionary).reduce(
      (accDictionary, checklistType) => ({
        ...accDictionary,
        [checklistType]: mode
      }),
      {}
    )
  }),
  {
    [TYPES.VEHICLE]: INTERVENTION_MODE.SALES,
    [TYPES.DISCOUNT]: INTERVENTION_MODE.SALES,
    [TYPES.TRADE_IN]: INTERVENTION_MODE.SALES
  }
);

export const CHECKLIST_SELECT_LABEL = createDictionary(
  {
    [TECHNICIAN]: 'checklists'
  },
  'lists'
);

/**
 * @type {Record<
 *   import("@arhs-fleetback/types/entities/checklists").ChecklistType,
 *   Array<import("@arhs-fleetback/types/entities/checklists").ChecklistType>
 * >}
 */
export const CHECKLIST_TYPES = createDictionary(
  {
    [SALES]: SALES_OFFER_CHECKLISTS,
    [PARTS]: [TYPES.PARTS, TYPES.PARTS_REMINDER_SCHEDULED]
  },
  [TYPES.WORKS, TYPES.REMINDER_SCHEDULED]
);

export const CHECKBOX_DEFAULT_KEY = 'accepted';

export const META_SOURCE = {
  SEARCH: 'search',
  EXTRAS_DASHBOARD: 'extras-dashboard',
  EXTRAS_STATS: 'extras-stats',
  DEFAULT_CHECKLISTS: 'defaultChecklists',
  EXTRAS_INTERVENTION_DETAILS: 'extras-intervention-details'
};

export const ADD_PREAPPROVED_BTN_TITLE = createDictionary(
  {
    [TECHNICIAN]: (
      <FormattedMessage id="page.fiche.preApproved.title" defaultMessage="Add pre-approved work / discount" />
    )
  },
  <FormattedMessage id="page.fiche.preApproved.items.btn" defaultMessage="Add pre-approved item / discount" />
);
